import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import colors, { shadows, borderRadius } from '../theme';

function IntegrationsPage() {
    const navigate = useNavigate();
    const [hoveredCard, setHoveredCard] = useState(null);

    const integrations = [
        {
            id: 'notion',
            name: 'Notion',
            description: 'Sync pages from Notion databases with AI-generated quizzes',
            icon: '📝',
            status: 'active',
            path: '/notion',
            color: colors.gray[900]
        },
        {
            id: 'google-docs',
            name: 'Google Docs',
            description: 'Import and sync content from Google Docs',
            icon: '📄',
            status: 'coming-soon',
            path: null,
            color: colors.blue[600]
        },
        {
            id: 'confluence',
            name: 'Confluence',
            description: 'Sync pages from Atlassian Confluence with real-time webhooks',
            icon: '🌊',
            status: 'active',
            path: '/integrations/confluence',
            color: colors.blue[500]
        }
    ];

    const handleCardClick = (integration) => {
        if (integration.status === 'active') {
            navigate(integration.path);
        } else {
            alert(`${integration.name} integration coming soon!`);
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: colors.gray[50], padding: '2rem' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '2rem' }}>
                    <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                        Integrations
                    </h1>
                    <p style={{ color: colors.gray[600], fontSize: '1rem' }}>
                        Connect your knowledge bases and documentation tools
                    </p>
                </div>

                {/* Integration Cards Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    marginBottom: '2rem'
                }}>
                    {integrations.map((integration) => (
                        <div
                            key={integration.id}
                            onClick={() => handleCardClick(integration)}
                            onMouseEnter={() => setHoveredCard(integration.id)}
                            onMouseLeave={() => setHoveredCard(null)}
                            style={{
                                backgroundColor: colors.white,
                                padding: '2rem',
                                borderRadius: borderRadius.lg,
                                boxShadow: hoveredCard === integration.id ? shadows.lg : shadows.sm,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                border: `2px solid ${hoveredCard === integration.id ? integration.color : colors.gray[200]}`,
                                transform: hoveredCard === integration.id ? 'translateY(-4px)' : 'translateY(0)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            {/* Status Badge */}
                            {integration.status === 'active' && (
                                <div style={{
                                    position: 'absolute',
                                    top: '1rem',
                                    right: '1rem',
                                    padding: '4px 12px',
                                    backgroundColor: colors.green[50],
                                    color: colors.green[700],
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    borderRadius: '12px',
                                    border: `1px solid ${colors.green[200]}`
                                }}>
                                    Active
                                </div>
                            )}

                            {integration.status === 'coming-soon' && (
                                <div style={{
                                    position: 'absolute',
                                    top: '1rem',
                                    right: '1rem',
                                    padding: '4px 12px',
                                    backgroundColor: colors.gray[100],
                                    color: colors.gray[600],
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    borderRadius: '12px',
                                    border: `1px solid ${colors.gray[300]}`
                                }}>
                                    Coming Soon
                                </div>
                            )}

                            {/* Icon */}
                            <div style={{
                                fontSize: '3.5rem',
                                marginBottom: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '80px',
                                height: '80px',
                                backgroundColor: `${integration.color}10`,
                                borderRadius: borderRadius.lg,
                                transition: 'transform 0.3s ease',
                                transform: hoveredCard === integration.id ? 'scale(1.1)' : 'scale(1)'
                            }}>
                                {integration.icon}
                            </div>

                            {/* Content */}
                            <h3 style={{
                                fontSize: '1.5rem',
                                fontWeight: '700',
                                marginBottom: '0.75rem',
                                color: colors.dark
                            }}>
                                {integration.name}
                            </h3>

                            <p style={{
                                fontSize: '0.95rem',
                                color: colors.gray[600],
                                lineHeight: '1.6',
                                marginBottom: '1.5rem'
                            }}>
                                {integration.description}
                            </p>

                            {/* Action Button */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                color: integration.status === 'active' ? integration.color : colors.gray[400],
                                fontWeight: '600',
                                fontSize: '0.95rem'
                            }}>
                                {integration.status === 'active' ? 'Configure' : 'Learn More'}
                                <span style={{ fontSize: '1.2rem' }}>→</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Info Section */}
                <div style={{
                    backgroundColor: colors.blue[50],
                    padding: '1.5rem 2rem',
                    borderRadius: borderRadius.lg,
                    border: `1px solid ${colors.blue[200]}`,
                    marginTop: '3rem'
                }}>
                    <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                        <div style={{ fontSize: '2rem' }}>💡</div>
                        <div>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', color: colors.dark }}>
                                Need a different integration?
                            </h3>
                            <p style={{ fontSize: '0.95rem', color: colors.gray[700], lineHeight: '1.6', margin: 0 }}>
                                We're always looking to add new integrations. Let us know what tools you use by sending us feedback!
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default IntegrationsPage;
